// Owner: WP04 (UX01). Application shell: desktop top navigation, mobile bottom navigation, session gating,
// scroll/focus restoration on Back, in-app reminders, toasts and the persistent player dock (WP03).
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Bookmark, Film, House, Radio, Search, Tv } from 'lucide-react';
import type { Preferences, Reminder } from '@tuned/contracts';
import { PlayerDock } from '../features/player/index.tsx';
import { onLogout, useUserState } from '../store/index.ts';
import { ButtonLink, ErrorState, LoadingState, Logo, Toaster, formatTime, formatWhen, isAuthError, resolveTimeZone, toast, useNow } from '../components/index.ts';
import { useActiveConnection, useSession } from './session.ts';
import { OfflineBanner } from '../sw/offline.tsx';
import '../styles/shell.css';

const NAV = [
  { to: '/', label: 'Home', icon: House, match: (p: string) => p === '/' },
  { to: '/live', label: 'Live', icon: Radio, match: (p: string) => p.startsWith('/live') || p.startsWith('/multiview') },
  { to: '/movies', label: 'Movies', icon: Film, match: (p: string) => p.startsWith('/movies') },
  { to: '/series', label: 'Series', icon: Tv, match: (p: string) => p.startsWith('/series') },
  { to: '/mytv', label: 'My TV', icon: Bookmark, match: (p: string) => p.startsWith('/mytv') },
];

export default function Shell() {
  const session = useSession();
  const connection = useActiveConnection();
  useRouteRestoration();
  useSignOutRedirect();

  if (session.isError && !session.data && isAuthError(session.error)) return <Navigate to="/setup" replace />;
  if (session.data && session.data.connections.length === 0) return <Navigate to="/setup" replace />;

  const reconnect = connection && connection.sessionState !== 'connected';
  return (
    <div className="shell">
      <a className="skip-link" href="#content">Skip to content</a>
      <Header profileName={session.data?.profile.name ?? ''} />
      <div id="content" tabIndex={-1} className="shell__content">
        <OfflineBanner />
        {reconnect ? (
          <div className="shell__banner" role="status">
            <p>
              {connection.sessionState === 'disconnected' ? 'You disconnected from your provider.' : 'Your provider session ended.'}{' '}
              Reconnect to watch; your saved favorites and history are still here.
            </p>
            <ButtonLink to="/setup" variant="primary" size="sm">Reconnect</ButtonLink>
          </div>
        ) : null}
        {session.isPending ? (
          <main className="page"><h1 className="visually-hidden">Tuned</h1><LoadingState label="Loading your workspace…" /></main>
        ) : session.isError && !session.data ? (
          <main className="page"><h1 className="visually-hidden">Tuned</h1><ErrorState error={session.error} title="Tuned could not start" onRetry={() => void session.refetch()} /></main>
        ) : (
          <Outlet />
        )}
      </div>
      <BottomNav />
      <StoreSync />
      <StartPage />
      <ReminderAlerts />
      <Toaster />
      <PlayerDock />
    </div>
  );
}

function Header({ profileName }: { profileName: string }) {
  const { pathname } = useLocation();
  return (
    <header className="shell__header">
      <Link to="/" className="shell__logo" aria-label="Tuned home"><Logo size={28} /></Link>
      <nav className="shell__nav" aria-label="Main">
        <ul>
          {NAV.map(({ to, label, icon: Icon, match }) => (
            <li key={to}>
              <NavLink to={to} className={match(pathname) ? 'shell__navlink is-active' : 'shell__navlink'} aria-current={match(pathname) ? 'page' : undefined}>
                <Icon size={20} aria-hidden />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="shell__tools">
        <SearchField />
        <Link to="/search" className="shell__tool shell__search-link" aria-label="Search"><Search size={22} aria-hidden /></Link>
        <RemindersButton />
        <Link to="/settings" className="shell__avatar" aria-label="Profile and settings" aria-current={pathname === '/settings' ? 'page' : undefined}>
          <span aria-hidden>{(profileName.trim()[0] ?? 'T').toUpperCase()}</span>
        </Link>
      </div>
    </header>
  );
}

function SearchField() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const current = pathname === '/search' ? params.get('q') ?? '' : '';
  const [q, setQ] = useState(current);
  useEffect(() => setQ(current), [current]);
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/search?q=${encodeURIComponent(term)}` : '/search');
  };
  return (
    <form role="search" className="shell__search" onSubmit={onSubmit}>
      <Search size={18} aria-hidden className="shell__search-icon" />
      <label htmlFor="global-search" className="visually-hidden">Search channels, movies, series and programs</label>
      <input id="global-search" type="search" placeholder="Search anything" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" enterKeyHint="search" />
    </form>
  );
}

function BottomNav() {
  const { pathname } = useLocation();
  return (
    <nav className="shell__bottom" aria-label="Main">
      <ul>
        {NAV.map(({ to, label, icon: Icon, match }) => (
          <li key={to}>
            <NavLink to={to} className={match(pathname) ? 'shell__tab is-active' : 'shell__tab'} aria-current={match(pathname) ? 'page' : undefined}>
              <Icon size={22} aria-hidden />
              <span>{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Guide deep link for a reminder (ids are opaque; no secrets in routes). */
export const reminderHref = (r: Reminder) =>
  `/live?${new URLSearchParams({ channel: r.channelId, program: r.programId, at: r.start }).toString()}`;

/** Upcoming reminders (scheduled or moved, not yet ended), soonest first. */
export function upcomingReminders(reminders: Reminder[] | undefined, now: number): Reminder[] {
  return (reminders ?? [])
    .filter((r) => (r.state === 'scheduled' || r.state === 'moved') && Date.parse(r.end) > now)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
}

function RemindersButton() {
  const { state } = useUserState();
  const now = useNow(30_000);
  const tz = resolveTimeZone(state?.preferences.timezone);
  const list = upcomingReminders(state?.reminders, now);
  const soon = list.filter((r) => Date.parse(r.start) - now < 60 * 60_000).length;
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); wrap.current?.querySelector('button')?.focus(); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const label = soon ? `Reminders, ${soon} starting within an hour` : 'Reminders';
  return (
    <div className="shell__reminders" ref={wrap}>
      <button type="button" className="shell__tool" aria-label={label} aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)}>
        <Bell size={22} aria-hidden />
        {soon ? <span className="shell__dot" aria-hidden /> : null}
      </button>
      {open ? (
        <div className="shell__popover" id={panelId} role="region" aria-label="Upcoming reminders">
          <h2>Reminders</h2>
          {list.length === 0 ? (
            <p className="muted">No upcoming reminders. Choose Remind me on a program in the guide.</p>
          ) : (
            <ul>
              {list.slice(0, 6).map((r) => (
                <li key={r.id}>
                  <Link to={reminderHref(r)}>
                    <span className="shell__rem-title">{r.title}</span>
                    <span className="shell__rem-when">{Date.parse(r.start) <= now ? `On now · until ${formatTime(r.end, tz)}` : formatWhen(r.start, tz)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link to="/mytv" className="see-all">Manage reminders in My TV</Link>
        </div>
      ) : null}
    </div>
  );
}

const fired = new Set<string>(); // in-memory only: no viewing data on disk (AUTH05 shared devices)

/** EPG04: in-app alert when a reminded program starts while Tuned is open. No background notifications in P1. */
function ReminderAlerts() {
  const { state } = useUserState();
  const navigate = useNavigate();
  const reminders = state?.reminders;
  useEffect(() => {
    const now = Date.now();
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (const r of upcomingReminders(reminders, now)) {
      const key = `${r.id}@${r.start}`;
      const wait = Date.parse(r.start) - now;
      if (fired.has(key) || wait > 24 * 3600_000 || wait < -2 * 60_000) continue;
      timers.push(setTimeout(() => {
        if (fired.has(key)) return;
        fired.add(key);
        toast({
          message: `${r.title} is starting now`,
          action: { label: 'Open', onClick: () => navigate(reminderHref(r)) },
          duration: 15_000,
        });
      }, Math.max(0, wait)));
    }
    return () => timers.forEach(clearTimeout);
  }, [reminders, navigate]);
  return null;
}

const scrollByKey = new Map<string, number>();
const focusByKey = new Map<string, string>();

const esc = (v: string) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v.replace(/["\\]/g, '\\$&'));
function focusSelector(el: Element | null): string | null {
  if (!(el instanceof HTMLElement) || el === document.body) return null;
  if (el.id) return `#${esc(el.id)}`;
  const key = el.closest('[data-focus-key]')?.getAttribute('data-focus-key');
  if (key) return `[data-focus-key="${esc(key)}"]`;
  const href = el.getAttribute('href');
  return href ? `a[href="${esc(href)}"]` : null;
}

/** UX01: restore scroll and keyboard focus on Back; otherwise start at the top and focus the new page heading. */
function useRouteRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const prev = useRef<{ key: string; pathname: string } | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = { key: location.key, pathname: location.pathname };
    if (!before || before.key === location.key) return;
    if (before.pathname === location.pathname && navType === 'REPLACE') return; // filter/tab changes keep position
    let tries = 0;
    let raf = 0;
    const restore = () => {
      if (navType === 'POP') {
        const y = scrollByKey.get(location.key) ?? 0;
        window.scrollTo(0, y);
        const sel = focusByKey.get(location.key);
        let el: HTMLElement | null = null;
        try { el = sel ? document.querySelector<HTMLElement>(sel) : null; } catch { /* stale selector */ }
        if (el) { el.focus({ preventScroll: true }); return; }
        if ((y > 0 && Math.abs(window.scrollY - y) > 2) || sel) { if (tries++ < 30) raf = requestAnimationFrame(restore); }
      } else {
        window.scrollTo(0, 0);
        const h1 = document.querySelector<HTMLElement>('#content h1');
        if (h1) { h1.tabIndex = -1; h1.focus({ preventScroll: true }); } else if (tries++ < 30) raf = requestAnimationFrame(restore);
      }
    };
    raf = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(raf);
  }, [location.key, location.pathname, navType]);
  useEffect(() => {
    const key = location.key;
    const save = () => {
      scrollByKey.set(key, window.scrollY);
      const sel = focusSelector(document.activeElement);
      if (sel) focusByKey.set(key, sel);
    };
    window.addEventListener('scroll', save, { passive: true });
    document.addEventListener('focusin', save);
    return () => { window.removeEventListener('scroll', save); document.removeEventListener('focusin', save); };
  }, [location.key]);
}

/** Keeps the user-state store scoped and its outbox (progress, favorites) flushing on every route (STORE03). */
function StoreSync() {
  useUserState();
  return null;
}

const START_ROUTES: Record<Preferences['startPage'], string> = { home: '/', live: '/live', movies: '/movies', series: '/series', mytv: '/mytv' };

/**
 * HOME01 preferred start page: a fresh landing on "/" (the router's initial entry, key 'default') opens the saved start
 * page once user state has loaded. Decided once per shell: Home clicks, Back and deep links are never redirected.
 */
function StartPage() {
  const { state } = useUserState();
  const { key, pathname } = useLocation();
  const navigate = useNavigate();
  const decided = useRef(false);
  useEffect(() => {
    if (decided.current || !state) return;
    decided.current = true;
    const to = START_ROUTES[state.preferences.startPage];
    if (key === 'default' && pathname === '/' && to && to !== '/') navigate(to, { replace: true });
  }, [state, key, pathname, navigate]);
  return null;
}

/** AUTH05: signing out here or in another tab (the store's cross-tab 'logout') returns this tab to setup. */
function useSignOutRedirect() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  useEffect(() => onLogout(() => { qc.clear(); navigate('/setup', { replace: true }); }), [navigate, qc]);
}
